import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import AddSourceMenuButton from '../../../../../components/menuButton/AddSourceMenuButton';
import SourcesFileListItem from './SourcesFileListItem';
import SourcesFolderList from './SourcesFolderList';
import SourcesRootDropOverlay from './SourcesRootDropOverlay';
import UnifiedSearchSummary from './UnifiedSearchSummary';
import SegmentItem from '../../../../../components/segmentItem';
import {unifiedHitToSegmentProps} from './unifiedHitToSegmentProps';
import {isSourceFileSelected} from './utils/sourceFileSelection';
import {renameSourcesFolder} from './sourcesApi';
import useCollectionMaterialViewer from '../../../../collection/viewer/useCollectionMaterialViewer';

const SEARCH_PAGE_SIZE = 20;

const SourcesSectionContent = ({
                                   activeView,
                                   headerTitle,
                                   filesSectionLabel,
                                   displayedSources,
                                   connectionState,
                                   isLoading,
                                   error,
                                   warning,
                                   isStale,
                                   userId,
                                   currentViewKey,
                                   selectedFileId,
                                   selectedFileSourceViewKey,
                                   onBack,
                                   onOpenCreateFolder,
                                   onFolderClick,
                                   onOpenFolderMenu,
                                   onFileClick,
                                   onFileDelete,
                                   onFileMoveRequest,
                                   onFileShare,
                                   onDropToCurrentView,
                                   onDropToFolder,
                                   onFileDragStart,
                                   onFolderDragStart,
                                   dropEnabled,
                                   dropToCurrentViewEnabled,
                                   canDropToFolder,
                                   canDragFolder,
                                   selectedCount,
                                   isFolderSelectable,
                                   isFolderSelected,
                                   onFolderSelectionToggle,
                                   isFileSelected,
                                   onFileSelectionToggle,
                                   flashFolderId,
                                   flashFolderToken,
                                   onBulkMoveRequest,
                                   onBulkNewFolderRequest,
                                   onBulkDeleteRequest,
                                   onBulkClearSelection,
                                   onOpenAddSource,
                                   urlImportEnabled = true,
                                   isSearchMode = false,
                                   searchResults = [],
                                   searchMeta = null,
                                   searchQuery = '',
                                   isSearching = false,
                                   searchError = null,
                                   onOpenSearchHit,
                               }) => {
    const [isDragOverSection, setIsDragOverSection] = React.useState(false);
    const dragDepthRef = React.useRef(0);
    const loadMoreSentinelRef = React.useRef(null);

    const [renameTarget, setRenameTarget] = React.useState(null);
    const [renameName, setRenameName] = React.useState('');
    const [renameError, setRenameError] = React.useState(null);
    const [isRenaming, setIsRenaming] = React.useState(false);

    const openRenameDialog = React.useCallback((file) => {
        setRenameTarget(file);
        setRenameName(file?.name || '');
        setRenameError(null);
    }, []);

    const closeRenameDialog = React.useCallback(() => {
        setRenameTarget(null);
        setRenameName('');
        setRenameError(null);
    }, []);

    const handleRenameSubmit = React.useCallback(async () => {
        const trimmed = renameName.trim();
        if (!trimmed || !renameTarget?.id || !userId) return;
        setIsRenaming(true);
        setRenameError(null);
        try {
            await renameSourcesFolder({folderId: renameTarget.id, userId, name: trimmed});
            closeRenameDialog();
        } catch (err) {
            setRenameError(err?.message || 'Rename failed.');
        } finally {
            setIsRenaming(false);
        }
    }, [closeRenameDialog, renameName, renameTarget, userId]);

    // Client-side "endless scroll": the unified API returns one large batch; we reveal
    // SEARCH_PAGE_SIZE results at a time as the sentinel scrolls into view.
    const [visibleCount, setVisibleCount] = React.useState(SEARCH_PAGE_SIZE);
    const totalResults = searchResults.length;

    // Reset the reveal window whenever the query or the underlying result set changes.
    React.useEffect(() => {
        setVisibleCount(SEARCH_PAGE_SIZE);
    }, [searchQuery, totalResults]);

    const visibleSearchResults = React.useMemo(
        () => (isSearchMode ? searchResults.slice(0, visibleCount) : searchResults),
        [isSearchMode, searchResults, visibleCount],
    );
    const hasMoreResults = isSearchMode && visibleCount < totalResults;

    React.useEffect(() => {
        if (!isSearchMode || !hasMoreResults) {
            return undefined;
        }
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setVisibleCount((current) => Math.min(current + SEARCH_PAGE_SIZE, totalResults));
            }
        }, {rootMargin: '200px'});
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isSearchMode, hasMoreResults, totalResults, isSearching]);


    const isLibraryPreset = activeView?.type === 'preset' && activeView?.id === 'my';
    const isCollectedMaterialsView = activeView?.id === 'collected-materials'
        || activeView?.branch === 'collected-materials'
        || activeView?.branch === 'collected';

    const {openCollectionMaterial, closeCollectionMaterial, viewerState} = useCollectionMaterialViewer();

    // When inside the Collected Materials branch and the item has a CM id,
    // open the full CM viewer overlay instead of the inline file viewer.
    // If any other file is clicked while the CM viewer is open, dismiss it first
    // so the store-driven inline viewer can take over.
    const handleFileClick = React.useCallback((file) => {
        if (isCollectedMaterialsView && file?.cmId) {
            openCollectionMaterial({materialId: file.cmId, materialKind: 'cm'});
            return;
        }
        if (viewerState) {
            closeCollectionMaterial();
        }
        onFileClick?.(file);
    }, [isCollectedMaterialsView, openCollectionMaterial, closeCollectionMaterial, viewerState, onFileClick]);
    // The upload + web-import affordances are available at the "Sources" root view,
    // the "My Files" preset, and inside any subfolder — so files/imports can be added
    // at every level where the user's own files live. Collected Materials folders are
    // virtual (1 Collection Task = 1 folder) and materials are added from the CT UI.
    const showUploadAffordances = !isCollectedMaterialsView
        && (activeView?.type === 'root' || isLibraryPreset || activeView?.type === 'folder');
    const showCreateFolder = !isSearchMode && !isCollectedMaterialsView;

    const handleAddDataClick = React.useCallback((kind) => {
        onOpenAddSource?.(kind);
    }, [onOpenAddSource]);

    const hasAnyItems = displayedSources.folders.length > 0 || displayedSources.files.length > 0;
    const showRootDropOverlay = isDragOverSection && activeView.type === 'root';
    const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = React.useState(null);

    const isFileDragEvent = React.useCallback((event) => {
        const types = event?.dataTransfer?.types;
        return Array.isArray(types) ? types.includes('Files') : types?.contains?.('Files');
    }, []);

    const resetSectionDragState = React.useCallback(() => {
        dragDepthRef.current = 0;
        setIsDragOverSection(false);
    }, []);

    const handleSectionDragOver = React.useCallback((event) => {
        if (!dropToCurrentViewEnabled || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
    }, [dropToCurrentViewEnabled, isFileDragEvent]);

    const handleSectionDragEnter = React.useCallback((event) => {
        if (!dropToCurrentViewEnabled || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDragOverSection(true);
    }, [dropToCurrentViewEnabled, isFileDragEvent]);

    const handleSectionDragLeave = React.useCallback((event) => {
        if (!dropToCurrentViewEnabled || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragOverSection(false);
        }
    }, [dropToCurrentViewEnabled, isFileDragEvent]);

    const handleSectionDropCapture = React.useCallback((event) => {
        if (!dropToCurrentViewEnabled || !isFileDragEvent(event)) {
            return;
        }
        resetSectionDragState();
    }, [dropToCurrentViewEnabled, isFileDragEvent, resetSectionDragState]);

    const handleSectionDrop = React.useCallback((event) => {
        if (!dropToCurrentViewEnabled || typeof onDropToCurrentView !== 'function' || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        onDropToCurrentView(event);
    }, [dropToCurrentViewEnabled, isFileDragEvent, onDropToCurrentView]);

    const openBulkMenu = React.useCallback((event) => {
        setBulkMenuAnchorEl(event.currentTarget);
    }, []);

    const closeBulkMenu = React.useCallback(() => {
        setBulkMenuAnchorEl(null);
    }, []);

    return (
        <Box
            data-testid="sources-section-content"
            sx={{
                overflowY: 'auto',
                flexGrow: 1,
                minHeight: 0,
                position: 'relative',
                transition: 'background-color 120ms ease, box-shadow 120ms ease',
                bgcolor: isDragOverSection ? '#dbe8ff' : '#e9ecef',
            }}
            onDragOverCapture={handleSectionDragOver}
            onDragEnterCapture={handleSectionDragEnter}
            onDragLeaveCapture={handleSectionDragLeave}
            onDropCapture={handleSectionDropCapture}
            onDrop={handleSectionDrop}
        >
            <Stack p={2} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1} minHeight={32}>
                    {activeView.type !== 'root' && (
                        <IconButton
                            aria-label="go back"
                            onClick={onBack}
                            size="small"
                        >
                            <ArrowBackIcon fontSize="small"/>
                        </IconButton>
                    )}

                    <Typography variant="body1" fontWeight={500} noWrap sx={{flexGrow: 1, minWidth: 0}}>
                        {headerTitle}
                    </Typography>

                    {activeView.type === 'folder' && isLoading && (
                        <CircularProgress size={16} thickness={5} aria-label="sources loading"/>
                    )}

                    {selectedCount > 0 && (
                        <>
                            <Button
                                size="small"
                                variant="text"
                                onClick={openBulkMenu}
                                endIcon={<ArrowDropDownIcon fontSize="small"/>}
                                sx={(theme) => ({
                                    height: 32,
                                    minHeight: 32,
                                    px: 1,
                                    minWidth: 0,
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    color: theme.palette.text.primary,
                                })}
                            >
                                {selectedCount} selected
                            </Button>
                            <Menu
                                anchorEl={bulkMenuAnchorEl}
                                open={Boolean(bulkMenuAnchorEl)}
                                onClose={closeBulkMenu}
                            >
                                <MenuItem onClick={() => {
                                    closeBulkMenu();
                                    onBulkMoveRequest?.();
                                }}
                                >
                                    Move to...
                                </MenuItem>
                                <MenuItem onClick={() => {
                                    closeBulkMenu();
                                    onBulkNewFolderRequest?.();
                                }}
                                >
                                    New folder with selected
                                </MenuItem>
                                <MenuItem onClick={() => {
                                    closeBulkMenu();
                                    onBulkDeleteRequest?.();
                                }}
                                >
                                    Delete
                                </MenuItem>
                                <MenuItem onClick={() => {
                                    closeBulkMenu();
                                    onBulkClearSelection?.();
                                }}
                                >
                                    Clear selection
                                </MenuItem>
                            </Menu>
                        </>
                    )}

                    {showUploadAffordances && (
                        <AddSourceMenuButton
                            ariaLabel="add source"
                            icon={<AddRoundedIcon fontSize="small"/>}
                            tooltip={(
                                <>
                                    Add source (files, URL, raw text{urlImportEnabled ? ', API' : ''})
                                    <br />
                                    Supported file types: PDF, DOCX, TXT, Audio, Video, Image
                                </>
                            )}
                            disabled={!userId}
                            onSelect={handleAddDataClick}
                            options={[
                                {key: 'file', label: 'Files', icon: <AttachFileRoundedIcon fontSize="small"/>},
                                ...(urlImportEnabled
                                    ? [{key: 'url', label: 'URL', icon: <LinkRoundedIcon fontSize="small"/>}]
                                    : []),
                                {key: 'paste', label: 'Raw text', icon: <NotesRoundedIcon fontSize="small"/>},
                                {key: 'api', label: 'API', icon: <CodeRoundedIcon fontSize="small"/>},
                            ]}
                        />
                    )}

                    {showCreateFolder && (
                        <Tooltip title="Create folder">
                            <span>
                                <IconButton
                                    aria-label="create folder"
                                    onClick={onOpenCreateFolder}
                                    size="small"
                                    disabled={!userId}
                                >
                                    <CreateNewFolderOutlinedIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                {error && !isLoading && !hasAnyItems && <Alert severity="error">{error}</Alert>}
                {!error && warning &&
                    <Alert severity="warning">{warning}{isStale ? ' Data may be outdated.' : ''}</Alert>}

                {isSearchMode ? (
                    <Stack spacing={1}>
                        <UnifiedSearchSummary/>
                        {isSearching && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <CircularProgress size={16} thickness={5} aria-label="searching"/>
                                <Typography variant="body2" color="text.secondary">Searching…</Typography>
                            </Stack>
                        )}
                        {searchError && !isSearching && (
                            <Alert severity="error">{searchError}</Alert>
                        )}
                        {!isSearching && !searchError && searchResults.length === 0 && (
                            <Typography color="text.secondary">No results found.</Typography>
                        )}
                        {searchResults.length > 0 && (
                            <Stack spacing={1} data-testid="sources-search-results">
                                {visibleSearchResults.map((hit, index) => {
                                    const key = `${hit?.documentStore || 's'}-${hit?.openTarget?.docId
                                        ?? hit?.openTarget?.canonicalFileId ?? hit?.openTarget?.reportId ?? 'x'}-${hit?.openTarget?.segmentId
                                        ?? hit?.sentenceHit?.range?.pmFrom ?? index}`;
                                    const segmentProps = unifiedHitToSegmentProps(hit, {query: searchQuery});
                                    if (!segmentProps) return null;
                                    // Card-level click opens the viewer; Save/Cite/tag controls
                                    // stopPropagation so they keep their own behavior.
                                    return (
                                        <SegmentItem
                                            key={key}
                                            props={{
                                                ...segmentProps,
                                                onContentClick: () => onOpenSearchHit?.(hit),
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                        {hasMoreResults && (
                            <Box
                                ref={loadMoreSentinelRef}
                                data-testid="sources-search-load-more-sentinel"
                                sx={{
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress size={16} thickness={5} aria-label="loading more results"/>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <>
                        {!error && !isLoading && !isStale && connectionState !== 'connecting'
                            && connectionState !== 'idle' && !hasAnyItems && (
                            <Typography>No sources found.</Typography>
                        )}

                        {!error && displayedSources.folders.length > 0 && (
                            <SourcesFolderList
                                folders={displayedSources.folders}
                                onFolderClick={onFolderClick}
                                onOpenFolderMenu={onOpenFolderMenu}
                                onFolderDrop={onDropToFolder}
                                dropEnabled={dropEnabled}
                                canDropToFolder={canDropToFolder}
                                draggable={dropEnabled}
                                canDragFolder={canDragFolder}
                                onFolderDragStart={onFolderDragStart}
                                isFolderSelectable={isFolderSelectable}
                                isFolderSelected={isFolderSelected}
                                onFolderSelectionToggle={onFolderSelectionToggle}
                                flashFolderId={flashFolderId}
                                flashFolderToken={flashFolderToken}
                            />
                        )}

                        {!error && displayedSources.files.length > 0 && (
                            <React.Fragment>
                                <Typography variant="body1" fontWeight={500}>{filesSectionLabel}</Typography>
                                <List dense sx={{p: 0}}>
                                    {displayedSources.files.map((file) => (
                                        <SourcesFileListItem
                                            key={file.id}
                                            file={file}
                                            isSelected={isSourceFileSelected({
                                                fileId: file.id,
                                                selectedFileId,
                                                currentViewKey,
                                                selectedFileSourceViewKey,
                                            })}
                                            onClick={() => handleFileClick(file)}
                                            onRename={openRenameDialog}
                                            onDelete={onFileDelete}
                                            onMove={onFileMoveRequest}
                                            onShare={onFileShare}
                                            userId={userId}
                                            draggable={dropEnabled}
                                            onDragStart={onFileDragStart}
                                            selectionEnabled={true}
                                            isSelectionChecked={isFileSelected?.(file)}
                                            onSelectionToggle={onFileSelectionToggle}
                                        />
                                    ))}
                                </List>
                            </React.Fragment>
                        )}
                    </>
                )}
            </Stack>

            {showRootDropOverlay && <SourcesRootDropOverlay/>}

            <Dialog open={Boolean(renameTarget)} onClose={closeRenameDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Rename</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="New name"
                        value={renameName}
                        onChange={(e) => { setRenameName(e.target.value); setRenameError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRenameSubmit(); } }}
                        error={Boolean(renameError)}
                        helperText={renameError || ''}
                        disabled={isRenaming}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRenameDialog} disabled={isRenaming}>Cancel</Button>
                    <Button
                        onClick={handleRenameSubmit}
                        disabled={isRenaming || !renameName.trim()}
                        variant="contained"
                    >
                        {isRenaming ? <CircularProgress size={18}/> : 'Rename'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default React.memo(SourcesSectionContent);
